import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import type { GtmWindow } from './analytics.types';

/**
 * Boots the official GTM snippet once per page load when the environment enables analytics (OM-807).
 * Injects the async gtm.js script into `<head>` and a noscript iframe at the start of `<body>`.
 */
@Injectable({ providedIn: 'root' })
export class GtmBootstrapService {
  private initialized = false;

  /**
   * Initializes `window.dataLayer` and injects GTM tags when `environment.gtm.enabled` is true.
   * Safe to call multiple times; subsequent calls are no-ops. Never throws.
   */
  initialize(): void {
    try {
      if (this.initialized || !environment.gtm.enabled) {
        return;
      }

      if (document.querySelector('script[data-gtm-bootstrap="true"]')) {
        this.initialized = true;
        return;
      }

      const containerId = environment.gtm.containerId;
      const gtmWindow = window as GtmWindow;
      gtmWindow.dataLayer = gtmWindow.dataLayer ?? [];
      gtmWindow.dataLayer.push({
        'gtm.start': Date.now(),
        event: 'gtm.js',
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
      script.dataset['gtmBootstrap'] = 'true';
      document.head.appendChild(script);

      const noscript = document.createElement('noscript');
      noscript.dataset['gtmBootstrap'] = 'true';
      noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
        containerId,
      )}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(noscript, document.body.firstChild);

      this.initialized = true;
    } catch {
      // GTM bootstrap failures must not block application startup.
    }
  }
}
